import { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll } from "framer-motion";
import { BRAND, waLink } from "@/lib/brand";
import { FaWhatsapp } from "react-icons/fa";
import { Phone, Menu, X, Globe } from "lucide-react";
import { useT, LANGS } from "@/lib/i18n";
import { Link } from "react-router-dom";
import { OWNER_ROUTES } from "@/lib/owners.i18n";

const OwnerLink = ({ className, onClick, testid }) => {
  const { t, lang } = useT();
  return (
    <Link to={OWNER_ROUTES[lang]} onClick={onClick} data-testid={testid} className={className}>
      {t("nav.owner")}
    </Link>
  );
};

function LanguageSwitcher() {
  const { lang, setLang } = useT();
  return (
    <div
      data-testid="language-switcher"
      className="flex items-center gap-1 rounded-full border border-gold/30 bg-navy-900/50 backdrop-blur-sm px-1 py-1"
    >
      <Globe className="h-3.5 w-3.5 text-gold/70 ml-2" />
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          data-testid={`lang-${l.code}`}
          aria-pressed={lang === l.code}
          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wider transition-[color,background-color,border-color,box-shadow,transform] ${
            lang === l.code
              ? "bg-gold text-navy-900"
              : "text-slate-400 hover:text-gold"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const { t } = useT();

  const links = [
    { key: "nav.services", href: "#services", testid: "services" },
    { key: "nav.pricing", href: "#pricing", testid: "pricing" },
    { key: "nav.request_viewing", href: "#request-viewing", testid: "request-viewing" },
    { key: "nav.faq", href: "#faq", testid: "faq" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[color,background-color,border-color,box-shadow,transform] duration-500 ${
        scrolled
          ? "bg-[#0B1733]/85 backdrop-blur-xl border-b border-gold/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
        <a
          href="#top"
          className="flex items-center gap-3 group shrink-0"
          data-testid="header-logo"
          onClick={() => setOpen(false)}
        >
          <div className="relative h-11 w-11 rounded-full overflow-hidden ring-1 ring-gold/40 group-hover:ring-gold transition-[color,background-color,border-color,box-shadow,transform]">
            <img
              src={BRAND.logoUrl}
              alt="Unity Real Estate"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="font-serif text-lg tracking-wide text-white">UNITY</div>
            <div className="text-[10px] tracking-[0.28em] text-gold uppercase">
              Real Estate
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.testid}`}
              className="text-sm text-slate-300 hover:text-gold transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-gold after:transition-[color,background-color,border-color,box-shadow,transform] hover:after:w-full"
            >
              {t(l.key)}
            </a>
          ))}
          <OwnerLink testid="nav-owner" className="inline-flex items-center gap-2 text-sm text-gold hover:text-gold-light transition-colors" />
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid="header-phone"
            className="hidden xl:inline-flex items-center gap-2 text-sm text-slate-300 hover:text-gold transition-colors"
          >
            <Phone className="h-4 w-4" /> {BRAND.phone}
          </a>
          <a
            href={waLink(t("wa.default"))}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-cta"
            className="hidden md:inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-navy-900 font-medium px-5 py-2.5 rounded-full transition-[color,background-color,border-color,box-shadow,transform] shadow-[0_4px_20px_rgba(201,162,75,0.25)] hover:-translate-y-0.5"
          >
            <FaWhatsapp className="h-4 w-4" /> {t("common.whatsapp")}
          </a>
          <button
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-gold/30 text-white hover:bg-gold/10 transition-colors"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <motion.div
        aria-hidden
        style={{ scaleX: scrollYProgress }}
        className="absolute bottom-0 left-0 right-0 h-px origin-left bg-gradient-to-r from-transparent via-gold to-transparent"
      />

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-nav"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden bg-[#0B1733]/95 backdrop-blur-xl border-t border-gold/10"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i }}
                  className="flex items-center justify-between text-slate-200 hover:text-gold py-3 px-2 border-b border-gold/10 last:border-b-0"
                >
                  <span className="text-base">{t(l.key)}</span>
                  <span className="text-gold/50">→</span>
                </motion.a>
              ))}
              <OwnerLink testid="mobile-nav-owner" onClick={() => setOpen(false)} className="flex items-center gap-2 text-gold py-3 px-2 border-b border-gold/10 text-base" />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 border border-gold/40 text-gold rounded-full py-3 text-sm font-medium"
                >
                  <Phone className="h-4 w-4" /> {t("common.call")}
                </a>
                <a
                  href={waLink(t("wa.default"))}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 bg-gold text-navy-900 rounded-full py-3 text-sm font-semibold"
                >
                  <FaWhatsapp className="h-4 w-4" /> {t("common.whatsapp")}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
